import React, { useState, useEffect, lazy, Suspense } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import "./utils/fontAwesomeSetup";
import { Navigation } from "./components/navigation";
import { Footer } from "./components/Footer";
import { Hero } from "./components/Hero";
import { PartnerCarousel } from "./components/PartnerCarousel";
import { GlobalNavbar } from "./components/GlobalNavbar";
import JsonData from "./data/data.json";
import "../src/styles/App.css";
import { seoConfig, organizationSchema, localBusinessSchema } from "./utils/seoConfig.js";

// ── Lazy-loaded: landing below-the-fold ─────────────────────────────────────
const Asesoramiento = lazy(() => import("./components/asesoramiento").then(m => ({ default: m.Asesoramiento })));
const About = lazy(() => import("./components/about").then(m => ({ default: m.About })));
const Testimonials = lazy(() => import("./components/testimonials").then(m => ({ default: m.Testimonials })));
const ElfsightSalesNotification = lazy(() => import("./components/ElfsightSalesNotification.jsx").then(m => ({ default: m.ElfsightSalesNotification })));
const Contact = lazy(() => import("./components/contact").then(m => ({ default: m.Contact })));
const Chatbot = lazy(() => import("./components/ChatBot").then(m => ({ default: m.Chatbot })));
const HowWeWork = lazy(() => import("./components/HowWeWork.jsx").then(m => ({ default: m.HowWeWork })));
const SocialProof = lazy(() => import("./components/SocialProof.jsx").then(m => ({ default: m.SocialProof })));
const Guarantees = lazy(() => import("./components/Guarantees.jsx").then(m => ({ default: m.Guarantees })));
const Differentials = lazy(() => import("./components/Differentials.jsx").then(m => ({ default: m.Differentials })));
const CookieConsent = lazy(() => import("./components/CookieConsent/CookieConsent.jsx"));

// ── Lazy-loaded: route pages ────────────────────────────────────────────────
const Cotizaciones = lazy(() => import("./components/Cotizaciones").then(m => ({ default: m.Cotizaciones })));
const DetalleCotizacion = lazy(() => import("./components/DetalleCotizacion").then(m => ({ default: m.DetalleCotizacion })));
const DetalleCotizacionSEO = lazy(() => import("./components/DetalleCotizacionSEO.jsx").then(m => ({ default: m.DetalleCotizacionSEO })));
const Graciasporsucompra = lazy(() => import("./components/graciasporsucompra").then(m => ({ default: m.Graciasporsucompra })));
const IndiceSitemap = lazy(() => import("./components/IndiceSitemaps.jsx"));
const Sitemap = lazy(() => import("./components/Sitemaps.jsx"));
const TerminosYCondiciones = lazy(() => import("./components/TerminosYCondiciones.jsx"));
const Politica = lazy(() => import("./components/Politica.jsx"));
const AboutComprarDominio = lazy(() => import("./components/aboutComprarDominio.jsx").then(m => ({ default: m.AboutComprarDominio })));
const AboutGoogleAds = lazy(() => import("./components/aboutGoogleAds.jsx").then(m => ({ default: m.AboutGoogleAds })));
const AboutReseñas = lazy(() => import("./components/aboutReseñas.jsx").then(m => ({ default: m.AboutReseñas })));
const AboutPosicionamiento = lazy(() => import("./components/aboutPosicionamiento.jsx").then(m => ({ default: m.AboutPosicionamiento })));
const AboutGoogleBussiness = lazy(() => import("./components/aboutGoogleBussiness.jsx").then(m => ({ default: m.AboutGoogleBussiness })));
const AboutOptimizacion = lazy(() => import("./components/aboutOptimizacion.jsx").then(m => ({ default: m.AboutOptimizacion })));
const AboutRedesSociales = lazy(() => import("./components/aboutRedesSociales.jsx").then(m => ({ default: m.AboutRedesSociales })));
const AboutLandingPage = lazy(() => import("./components/aboutLandingPage.jsx").then(m => ({ default: m.AboutLandingPage })));
const EBook = lazy(() => import("./components/EBook.jsx").then(m => ({ default: m.EBook })));
const Sistema55 = lazy(() => import("./components/Sistema55.jsx").then(m => ({ default: m.Sistema55 })));
const ConfirmaSuscripcion = lazy(() => import("./components/ConfirmaSuscripcion.jsx").then(m => ({ default: m.ConfirmaSuscripcion })));
const Verdiore = lazy(() => import("./components/verdiore.jsx").then(m => ({ default: m.Verdiore })));
const Services = lazy(() => import("./components/services.jsx").then(m => ({ default: m.Services })));
const Gallery = lazy(() => import("./components/gallery.jsx").then(m => ({ default: m.Gallery })));
const Kit40 = lazy(() => import("./components/Kit40.jsx").then(m => ({ default: m.Kit40 })));
const CybersecuritySpain = lazy(() => import("./pages/CybersecuritySpain"));
const AuditoriaSeguridadWeb = lazy(() => import("./pages/AuditoriaSeguridadWeb"));
const CumplimientoNIS2 = lazy(() => import("./pages/CumplimientoNIS2"));
const AuditoriaGDPR = lazy(() => import("./pages/AuditoriaGDPR"));
const TestVulnerabilidades = lazy(() => import("./pages/TestVulnerabilidades"));
const SeguridadEcommerce = lazy(() => import("./pages/SeguridadEcommerce"));
const AvisoLegal = lazy(() => import("./components/AvisoLegal.jsx"));
const PoliticaCookies = lazy(() => import("./components/PoliticaCookies.jsx"));
const PoliticaSeguridad = lazy(() => import("./components/PoliticaSeguridad.jsx"));
const CondicionesAceptacionServicio = lazy(() => import("./components/CondicionesAceptacionServicio.jsx"));
const DesarrolloWeb = lazy(() => import("./pages/DesarrolloWeb.jsx"));
const GeneradorPresupuestos = lazy(() => import("./pages/GeneradorPresupuestos.jsx"));
const NotFound = lazy(() => import("./pages/NotFound.jsx"));

const LandingPage = ({ data }) => (
  <>
    <CookieConsent />
    <PartnerCarousel />
    <Hero />
    <Suspense fallback={null}>
      <Asesoramiento data={data.Asesoramiento} />
      <HowWeWork />
      <Differentials />
      <Guarantees />
      <About data={data.About} />
      <Testimonials />
      <SocialProof />
      <ElfsightSalesNotification />
      <Contact />
      <Chatbot />
    </Suspense>
    <Footer />
  </>
);

const BASE_URL = "https://toledoconsultora.com";

const pageSEO = {
  "/": seoConfig.home,
  "/desarrollo-web": {
    ...seoConfig.services,
    title: "Desarrollo Web Profesional, Ciberseguridad y SEO | Toledo Consultora IT",
    description: "Desarrollo web profesional con React y Node.js, ecommerce seguro, auditorias OWASP y SEO tecnico para empresas en Espana y LATAM.",
    canonical: `${BASE_URL}/desarrollo-web`,
  },
  "/kit-4-0": {
    title: "KIT 4.0 para PyMEs Industriales | Toledo Consultora IT",
    description: "Digitalización industrial con Programa KIT 4.0: software, ERP, apps Android, cloud, VPN y ciberseguridad con cofinanciamiento de hasta el 50%.",
    keywords: "KIT 4.0, industria 4.0, PyMEs industriales, crédito fiscal, proveedores PEC, transformación digital industrial",
    canonical: `${BASE_URL}/kit-4-0`,
    ogTitle: "KIT 4.0: digitaliza tu industria pagando solo el 50% neto",
    ogDescription: "Toledo Consultora acompaña a PyMEs industriales en diagnóstico, reserva de cupo e implementación de soluciones KIT 4.0.",
    ogImage: `${BASE_URL}/images/mockups/optimized/servicios-2-900.jpg`,
  },
  "/servicios-profesionales": {
    ...seoConfig.cotizaciones,
    canonical: `${BASE_URL}/servicios-profesionales`,
  },
  "/asesoramiento-profesional": {
    title: "Asesoramiento Profesional IT | Toledo Consultora IT",
    description: "Asesoramiento profesional para desarrollo web, ciberseguridad, SEO tecnico y estrategia digital para empresas.",
    canonical: `${BASE_URL}/asesoramiento-profesional`,
  },
  "/acerca-de-nosotros": {
    ...seoConfig.about,
    canonical: `${BASE_URL}/acerca-de-nosotros`,
  },
  "/nuestros-servicios": {
    ...seoConfig.services,
    canonical: `${BASE_URL}/nuestros-servicios`,
  },
  "/nuestros-proyectos": {
    ...seoConfig.portfolio,
    canonical: `${BASE_URL}/nuestros-proyectos`,
  },
  "/contacto": {
    ...seoConfig.contact,
    canonical: `${BASE_URL}/contacto`,
  },
  "/e-book-gratuito": {
    title: "E-book Gratuito | Toledo Consultora IT",
    description: "Descarga gratuita de recursos para ecommerce, SEO y estrategia digital.",
    canonical: `${BASE_URL}/e-book-gratuito`,
  },
  "/sistema-5-5": {
    title: "Sistema 5-5 | Toledo Consultora IT",
    description: "Sistema 5-5 para contenido, impresiones organicas y crecimiento digital.",
    canonical: `${BASE_URL}/sistema-5-5`,
  },
  "/verdiore": {
    title: "Verdiore | Caso de Exito Ecommerce | Toledo Consultora IT",
    description: "Landing y estrategia digital para Verdiore desarrollada por Toledo Consultora IT.",
    canonical: `${BASE_URL}/verdiore`,
  },
  "/cybersecurity-audit-spain": {
    title: "Auditoria de Ciberseguridad en Espana | Toledo Consultora IT",
    description: "Auditoria de ciberseguridad para empresas en Espana: OWASP, NIS2, GDPR, hardening y reporte ejecutivo.",
    canonical: `${BASE_URL}/cybersecurity-audit-spain`,
  },
  "/auditoria-de-seguridad-web": {
    title: "Auditoria de Seguridad Web OWASP | Toledo Consultora IT",
    description: "Auditoria de seguridad web con revision OWASP, cabeceras, exposicion tecnica y plan de remediacion.",
    canonical: `${BASE_URL}/auditoria-de-seguridad-web`,
  },
  "/cumplimiento-nis2": {
    title: "Cumplimiento NIS2 para Empresas | Toledo Consultora IT",
    description: "Consultoria de cumplimiento NIS2, gestion de riesgos, controles tecnicos y documentacion para empresas.",
    canonical: `${BASE_URL}/cumplimiento-nis2`,
  },
  "/auditoria-gdpr": {
    title: "Auditoria GDPR y Proteccion de Datos | Toledo Consultora IT",
    description: "Auditoria GDPR para sitios web y empresas: privacidad, consentimiento, formularios, cookies y medidas tecnicas.",
    canonical: `${BASE_URL}/auditoria-gdpr`,
  },
  "/test-de-vulnerabilidades": {
    title: "Test de Vulnerabilidades Web | Toledo Consultora IT",
    description: "Test de vulnerabilidades para detectar fallos de seguridad, configuraciones debiles y riesgos web prioritarios.",
    canonical: `${BASE_URL}/test-de-vulnerabilidades`,
  },
  "/seguridad-ecommerce": {
    title: "Seguridad para Ecommerce | Toledo Consultora IT",
    description: "Seguridad para tiendas online, pagos, formularios, autenticacion, proteccion de datos y hardening tecnico.",
    canonical: `${BASE_URL}/seguridad-ecommerce`,
  },
  "/about-google-ads": seoConfig.googleAds,
  "/about-posicionamiento": seoConfig.seoPositioning,
  "/about-google-business": seoConfig.googleBusiness,
  "/about-landing-page": seoConfig.landingPages,
  "/about-reseñas": seoConfig.reviews,
  "/about-redes-sociales": seoConfig.socialMedia,
  "/about-optimizacion": {
    title: "Optimizacion Web y Core Web Vitals | Toledo Consultora IT",
    description: "Optimizacion web tecnica para mejorar velocidad, experiencia de usuario, Core Web Vitals e indexabilidad.",
    canonical: `${BASE_URL}/about-optimizacion`,
  },
  "/about-comprar-dominio": seoConfig.dominio,
  "/terminos-y-condiciones": {
    title: "Terminos y Condiciones | Toledo Consultora IT",
    description: "Terminos y condiciones de uso y contratacion de los servicios de Toledo Consultora IT.",
    canonical: `${BASE_URL}/terminos-y-condiciones`,
  },
  "/condiciones-de-aceptacion-del-servicio": {
    title: "Condiciones de Aceptacion del Servicio | Toledo Consultora IT",
    description: "Condiciones de aceptacion, alcance y contratacion de servicios profesionales.",
    canonical: `${BASE_URL}/condiciones-de-aceptacion-del-servicio`,
  },
  "/politica-de-privacidad": {
    title: "Politica de Privacidad | Toledo Consultora IT",
    description: "Politica de privacidad y tratamiento de datos personales de Toledo Consultora IT.",
    canonical: `${BASE_URL}/politica-de-privacidad`,
  },
  "/politica-de-seguridad": {
    title: "Politica de Seguridad | Toledo Consultora IT",
    description: "Politica de seguridad de Toledo Consultora IT para uso del sitio y servicios digitales.",
    canonical: `${BASE_URL}/politica-de-seguridad`,
  },
  "/politica-de-cookies": {
    title: "Politica de Cookies | Toledo Consultora IT",
    description: "Informacion sobre cookies, consentimiento y tecnologias usadas en el sitio.",
    canonical: `${BASE_URL}/politica-de-cookies`,
  },
  "/aviso-legal": {
    title: "Aviso Legal | Toledo Consultora IT",
    description: "Aviso legal e informacion general de Toledo Consultora IT.",
    canonical: `${BASE_URL}/aviso-legal`,
  },
};

const noindexRoutes = new Set([
  "/gracias-por-su-compra",
  "/confirma-suscripcion",
  "/indice-sitemap",
  "/indice-del-sitio",
]);

const getRouteSEO = (pathname) => {
  if (pathname.startsWith("/detalle/")) {
    return {
      title: "Detalle del servicio | Toledo Consultora IT",
      description: "Detalle comercial del servicio solicitado.",
      canonical: `${BASE_URL}${pathname}`,
      robots: "noindex, follow",
    };
  }

  if (pathname.startsWith("/servicios-consultora-toledo/")) {
    return {
      title: "Servicio profesional | Toledo Consultora IT",
      description: "Pagina comercial de servicio. Consulta los servicios profesionales principales de Toledo Consultora IT.",
      canonical: `${BASE_URL}/servicios-profesionales`,
      robots: "noindex, follow",
    };
  }

  if (noindexRoutes.has(pathname)) {
    return {
      title: "Pagina auxiliar | Toledo Consultora IT",
      description: "Pagina auxiliar de Toledo Consultora IT.",
      canonical: `${BASE_URL}${pathname}`,
      robots: "noindex, follow",
    };
  }

  return pageSEO[pathname] || {
    title: "Toledo Consultora IT · SEO y Ciberseguridad en España",
    description: "Consultora IT en España: auditorías de ciberseguridad OWASP, posicionamiento SEO y desarrollo web. Diagnóstico con informe desde USD 50.",
    canonical: `${BASE_URL}${pathname}`,
    robots: pathname === "/" ? "index, follow" : "noindex, follow",
  };
};

const RouteSEO = () => {
  const location = useLocation();
  const seo = getRouteSEO(location.pathname);
  const robots = seo.robots || "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1";

  return (
    <Helmet>
      <html lang="es" />
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      {seo.keywords && <meta name="keywords" content={seo.keywords} />}
      <meta name="robots" content={robots} />
      <link rel="canonical" href={seo.canonical} />
      <meta property="og:title" content={seo.ogTitle || seo.title} />
      <meta property="og:description" content={seo.ogDescription || seo.description} />
      <meta property="og:url" content={seo.canonical} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="es_AR" />
      <meta property="og:image" content={seo.ogImage || `${BASE_URL}/img/og-image.png`} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seo.ogTitle || seo.title} />
      <meta name="twitter:description" content={seo.ogDescription || seo.description} />
      <meta name="twitter:image" content={seo.ogImage || `${BASE_URL}/img/twitter-image.png`} />
      <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
    </Helmet>
  );
};

const App = () => {
  const [landingPageData, setLandingPageData] = useState({});
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    setLandingPageData(JsonData);
  }, []);

  useEffect(() => {
    if (location.hash) {
      const sectionId = location.hash.substring(1);
      const element = document.getElementById(sectionId);

      if (element) {
        const navbarHeight = 220;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;

        setTimeout(() => {
          window.scrollTo({ top: offsetPosition, behavior: "smooth" });
        }, 100);
      }
    }
  }, [location]);

  // Scroll to top on route change when there's no hash (fixes landing at footer)
  useEffect(() => {
    if (!location.hash) {
      // small timeout to allow new route/component to render
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
    }
  }, [location.pathname, location.hash]);

  return (
    <>
      <RouteSEO />
      {isHome ? <Navigation /> : <GlobalNavbar />}

      <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<LandingPage data={landingPageData} />} />
          <Route path="/detalle/:id" element={<><DetalleCotizacion /><Footer /></>} />
          <Route path="/servicios-consultora-toledo/:id" element={<><DetalleCotizacionSEO /><Footer /></>} />
          <Route path="/servicios-profesionales" element={<><Cotizaciones asPageHeading /><Footer /></>} />
          <Route path="/asesoramiento-profesional" element={<><Asesoramiento data={landingPageData.Asesoramiento} /><Footer /></>} />
          <Route path="/acerca-de-nosotros" element={<><About data={landingPageData.About} /><Footer /></>} />
          <Route path="/nuestros-servicios" element={<><Services data={landingPageData.Services} /><Footer /></>} />
          <Route path="/nuestros-proyectos" element={<><Gallery data={landingPageData.Gallery} /><Footer /></>} />
          <Route path="/indice-sitemap" element={<><IndiceSitemap /><Footer /></>} />
          <Route path="/indice-del-sitio" element={<><Sitemap /><Footer /></>} />
          <Route path="/gracias-por-su-compra" element={<><Graciasporsucompra /><Footer /></>} />
          <Route path="/terminos-y-condiciones" element={<><TerminosYCondiciones /><Footer /></>} />
          <Route path="/condiciones-de-aceptacion-del-servicio" element={<><CondicionesAceptacionServicio /><Footer /></>} />
          <Route path="/politica-de-privacidad" element={<><Politica /><Footer /></>} />
          <Route path="/politica-de-seguridad" element={<><PoliticaSeguridad /><Footer /></>} />
          <Route path="/politica-de-cookies" element={<><PoliticaCookies /><Footer /></>} />
          <Route path="/contacto" element={<><Contact /><Footer /></>} />
          <Route path="/aviso-legal" element={<><AvisoLegal /><Footer /></>} />
          <Route path="/about-comprar-dominio" element={<><AboutComprarDominio /><Footer /></>} />
          <Route path="/about-google-ads" element={<><AboutGoogleAds /><Footer /></>} />
          <Route path="/about-reseñas" element={<><AboutReseñas /><Footer /></>} />
          <Route path="/about-posicionamiento" element={<><AboutPosicionamiento /><Footer /></>} />
          <Route path="/about-google-business" element={<><AboutGoogleBussiness /><Footer /></>} />
          <Route path="/about-optimizacion" element={<><AboutOptimizacion /><Footer /></>} />
          <Route path="/about-redes-sociales" element={<><AboutRedesSociales /><Footer /></>} />
          <Route path="/about-landing-page" element={<><AboutLandingPage /><Footer /></>} />
          <Route path="/e-book-gratuito" element={<><EBook /><Footer /></>} />
          <Route path="/sistema-5-5" element={<><Sistema55 /><Footer /></>} />
          <Route path="/confirma-suscripcion" element={<><ConfirmaSuscripcion /><Footer /></>} />
          <Route path="/verdiore" element={<><Verdiore /><Footer /></>} />
          <Route path="/cybersecurity-audit-spain" element={<><CybersecuritySpain /><Footer /></>} />
          <Route path="/auditoria-de-seguridad-web" element={<><AuditoriaSeguridadWeb /><Footer /></>} />
          <Route path="/cumplimiento-nis2" element={<><CumplimientoNIS2 /><Footer /></>} />
          <Route path="/auditoria-gdpr" element={<><AuditoriaGDPR /><Footer /></>} />
          <Route path="/test-de-vulnerabilidades" element={<><TestVulnerabilidades /><Footer /></>} />
          <Route path="/seguridad-ecommerce" element={<><SeguridadEcommerce /><Footer /></>} />
          <Route path="/desarrollo-web" element={<><DesarrolloWeb /><Footer /></>} />
          <Route path="/kit-4-0" element={<><Kit40 /><Footer /></>} />
          <Route path="/generador-de-presupuestos" element={<><GeneradorPresupuestos /><Footer /></>} />
          <Route path="*" element={<><NotFound /><Footer /></>} />
        </Routes>
      </Suspense>
    </>
  );
};

export default App;
